import React, { useState } from 'react';
import { useCategoriesViewModel } from '../../viewmodels/useCategoriesViewModel';
import { CategoryMenuConfig, NavigationItem } from '../../themes/themeTypes';
import { cn } from '../../utils/cn';
import { ProductCategory } from '../../types';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import LocaleSwitcher from '../layout/LocaleSwitcher';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  navigationItems: NavigationItem[];
  categoryMenuConfig?: CategoryMenuConfig;
  navFontWeightClass: string;
  navTextTransformClass: string;
}

const MobileCategorySection: React.FC<{
  categories: ProductCategory[];
  config: CategoryMenuConfig;
  navFontWeightClass: string;
  navTextTransformClass: string;
}> = ({ categories, config, navFontWeightClass, navTextTransformClass }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (!config.enabled || !categories.length) return null;

  return (
    <ul className="list-none p-0 m-0 space-y-1" data-testid="mobile-category-menu">
      {categories.map((category) => {
        const children = (category.category_children || [])
          .filter((c) => c.is_active && !c.is_internal)
          .sort((a, b) => a.rank - b.rank);
        const hasChildren = children.length > 0;
        const isExpanded = expandedId === category.id;

        return (
          <li key={category.id}>
            <div className="group flex items-center">
              <a
                href={`/categories/${category.handle}`}
                className={cn(
                  'flex-1 block px-2 py-2.5 text-base text-text-primary no-underline hover:text-primary transition-colors',
                  navFontWeightClass,
                  navTextTransformClass
                )}
                data-testid="mobile-category-link"
              >
                {category.name}
              </a>
              {hasChildren && (
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : category.id)}
                  className="p-2.5 text-text-secondary group-hover:text-primary hover:text-primary bg-transparent border-none cursor-pointer rounded-sm"
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category.name}`}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={cn(
                      'transition-transform duration-200 ease-out',
                      isExpanded ? 'rotate-90' : 'group-hover:translate-x-1'
                    )}
                  >
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </button>
              )}
            </div>
            {hasChildren && isExpanded && (
              <ul className="list-none p-0 m-0 ml-4 space-y-0.5">
                {children.map((child) => (
                  <li key={child.id}>
                    <a
                      href={`/categories/${child.handle}`}
                      className="block px-2 py-2 text-sm text-text-secondary no-underline hover:text-primary transition-colors"
                      data-testid="mobile-category-child-link"
                    >
                      {child.name}
                      {config.showDescriptions && child.description && (
                        <span className="block text-xs text-text-light mt-0.5 line-clamp-1">
                          {child.description}
                        </span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const MobileNav: React.FC<MobileNavProps> = ({
  isOpen,
  onClose,
  navigationItems,
  categoryMenuConfig,
  navFontWeightClass,
  navTextTransformClass,
}) => {
  const { topLevelCategories } = useCategoriesViewModel();
  const focusTrapRef = useFocusTrap(isOpen, onClose);

  const menuConfig = categoryMenuConfig || {
    style: 'dropdown' as const,
    enabled: true,
    showImages: false,
    showDescriptions: false,
    position: 'in-nav' as const,
  };

  return (
    <>
      {/* Backdrop overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
          data-testid="mobile-menu-backdrop"
        />
      )}

      {/* Slide-in drawer */}
      <div
        ref={focusTrapRef}
        className={cn(
          'fixed top-0 left-0 h-full w-[280px] max-w-[85vw] bg-[var(--color-bg)] z-50 shadow-xl overflow-y-auto md:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        style={{
          visibility: isOpen ? 'visible' : 'hidden',
          transition: isOpen
            ? 'transform 300ms ease-in-out, visibility 0s'
            : 'transform 300ms ease-in-out, visibility 0s 300ms',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        aria-hidden={!isOpen}
        data-testid="mobile-nav-drawer"
      >
        <nav aria-label="Mobile navigation links">
        {/* Drawer Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <span className="text-lg font-semibold text-text-primary">Menu</span>
          <button
            onClick={onClose}
            className="p-2 -m-2 text-text-secondary hover:text-primary bg-transparent border-none cursor-pointer rounded-sm"
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation Links — top-level nav items + categories in one unified list,
            mimicking carolinesvedbom.com's mobile drawer. */}
        <div className="p-4">
          <ul className="list-none p-0 m-0 space-y-1">
            {navigationItems.map((item, index) => (
              <li key={index}>
                <a
                  href={item.path}
                  className={cn(
                    'block px-2 py-2.5 text-base text-text-primary no-underline hover:text-primary transition-colors',
                    navFontWeightClass,
                    navTextTransformClass
                  )}
                  data-testid="mobile-nav-link"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          {menuConfig.enabled && (
            <MobileCategorySection
              categories={topLevelCategories}
              config={menuConfig}
              navFontWeightClass={navFontWeightClass}
              navTextTransformClass={navTextTransformClass}
            />
          )}

          {/* Locale chooser — last item in the drawer (issue #140).
              Same unified chooser as the desktop header, rendered inline. */}
          <LocaleSwitcher presentation="inline" />
        </div>
        </nav>
      </div>
    </>
  );
};

export default MobileNav;
